import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Lock, Mail } from 'lucide-react'
import { AdminService } from '../../services/adminService'
import { supabase } from '../../lib/supabase'
import { SessionManager } from '../../services/sessionManager'
import { getAdminReturnPath } from '../../services/adminSessionAccess'

const AdminLogin = ({ onLoginSuccess }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [magicLinkSent, setMagicLinkSent] = useState(false)
  const [processingCallback, setProcessingCallback] = useState(false)

  // MFA challenge state
  const [showMFAChallenge, setShowMFAChallenge] = useState(false)
  const [mfaCode, setMFACode] = useState('')
  const [mfaFactorId, setMFAFactorId] = useState('')
  const [mfaVerifying, setMfaVerifying] = useState(false)

  // MFA reset state
  const [showMFAReset, setShowMFAReset] = useState(false)
  const [mfaResetEmail, setMFAResetEmail] = useState('')
  const [mfaResetLoading, setMFAResetLoading] = useState(false)
  const [mfaResetSent, setMFAResetSent] = useState(false)

  const handleMagicLinkCallback = React.useCallback(async () => {
    try {
      setProcessingCallback(true)
      setError('')

      console.log('🔗 Processing magic link callback...')

      // Check if this is an MFA reset flow (from localStorage, not URL hash)
      const isMFAReset = localStorage.getItem('admin_mfa_reset_requested') === 'true'
      
      if (isMFAReset) {
        console.log('🔄 MFA reset flag detected in localStorage')
      }

      // Verify admin profile
      const result = await AdminService.verifyAdminProfile()

      if (!result.success) {
        setError(result.error)
        setProcessingCallback(false)
        return
      }

      console.log('✅ Admin profile verified')

      // MFA RESET FLOW: Call Edge Function to delete MFA factors
      if (isMFAReset) {
        console.log('🔄 MFA reset requested - calling Edge Function...')

        try {
          // Get current session for the JWT
          const { data: { session } } = await supabase.auth.getSession()
          
          if (!session) {
            setError('Session not found. Please try again.')
            setProcessingCallback(false)
            return
          }

          // Call the Edge Function to reset MFA
          const { data, error: edgeError } = await supabase.functions.invoke('reset-mfa', {
            body: {}
          })

          if (edgeError) {
            console.error('❌ Edge Function error:', edgeError)
            setError('Failed to reset MFA: ' + edgeError.message)
            setProcessingCallback(false)
            return
          }

          console.log('✅ Edge Function result:', data)

          // Clear all MFA-related session storage
          localStorage.removeItem('admin_mfa_verified_session_id')
          localStorage.removeItem('admin_mfa_challenge_completed')
          sessionStorage.removeItem('admin_mfa_verified_session_id')
          sessionStorage.removeItem('admin_mfa_challenge_required')
          sessionStorage.removeItem('admin_mfa_factor_id')

          // Clear the reset flag so it doesn't trigger again
          localStorage.removeItem('admin_mfa_reset_requested')
          localStorage.removeItem('admin_mfa_reset_email')

          console.log('✅ MFA reset complete, redirecting to MFA setup...')

          // Redirect to MFA setup
          const nextPath = getAdminReturnPath(location)
          navigate('/admin/mfa-setup', { replace: true, state: { from: nextPath, mfaReset: true } })
          return

        } catch (resetError) {
          console.error('❌ MFA reset error:', resetError)
          setError('Failed to reset MFA. Please try again or contact support.')
          setProcessingCallback(false)
          return
        }
      }

      // FIRST: Check if user has MFA enrolled in Supabase
      const { data: factors, error: factorsError } = await supabase.auth.mfa.listFactors()
      if (factorsError) throw factorsError
      const verifiedTotpFactors = (factors?.totp || []).filter(f => f.verified === true || f.status === 'verified')

      if (verifiedTotpFactors.length > 0) {
        // Step 1: Get active session
        const { data: { session } } = await supabase.auth.getSession()

        // Step 2: Check for existing trust (Session-Bonded)
        const { data: assurance, error: assuranceError } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel()
        if (assuranceError) throw assuranceError
        const isSessionTrusted = session && assurance?.currentLevel === 'aal2'

        if (isSessionTrusted) {
          console.log('✅ Session already trusted, restoring destination...')

          const nextPath = getAdminReturnPath(location)
          localStorage.removeItem('admin_login_next_path')

          onLoginSuccess(result.user)
          setTimeout(() => {
            navigate(nextPath, { replace: true })
          }, 500)
          return
        }

        // Returning user with MFA - show challenge
        const factorId = verifiedTotpFactors[0].id
        setMFAFactorId(factorId)
        sessionStorage.setItem('admin_mfa_challenge_required', 'true')
        sessionStorage.setItem('admin_mfa_factor_id', factorId)

        setShowMFAChallenge(true)
        setProcessingCallback(false)
        return
      }

      // SECOND: Handle Setup redirects
      if (verifiedTotpFactors.length === 0) {
        console.log('📱 MFA setup required, redirecting...')
        const nextPath = getAdminReturnPath(location)
        navigate('/admin/mfa-setup', { replace: true, state: { from: nextPath } })
        return
      }

      // No MFA required, proceed to dashboard
      const targetPath = getAdminReturnPath(location)
      localStorage.removeItem('admin_login_next_path')
      onLoginSuccess(result.user)
      navigate(targetPath, { replace: true })

    } catch (error) {
      console.error('❌ Magic link callback error:', error)
      setError('Failed to complete login.')
      setProcessingCallback(false)
    }
  }, [navigate, onLoginSuccess, location])


  useEffect(() => {
    // AUTO-REDIRECT: Skip login form if already verified in this session
    const checkAuthStatus = async () => {
      if (processingCallback || showMFAChallenge || showMFAReset || localStorage.getItem('admin_mfa_reset_requested') === 'true') return
      try {
        const { data: { session }, error } = await supabase.auth.getSession()
        if (error || !session) return
        const { data: assurance, error: assuranceError } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel()
        if (assuranceError) return
        if (assurance?.currentLevel === 'aal2') {
          navigate(getAdminReturnPath(location), { replace: true })
        } else {
          const { data: factors, error: factorsError } = await supabase.auth.mfa.listFactors()
          if (factorsError) return
          const factor = (factors?.totp || []).find(f => f.status === 'verified' || f.verified === true)
          if (factor) { setMFAFactorId(factor.id); setShowMFAChallenge(true) }
        }
      } catch (_) { setError('Could not check your session. Please try again shortly.') }
    }

    // Check for magic link callback in URL hash
    const hashParams = new URLSearchParams(window.location.hash.substring(1))
    const accessToken = hashParams.get('access_token')
    const type = hashParams.get('type')

    const mfaChallengeRequired = sessionStorage.getItem('admin_mfa_challenge_required') === 'true'
    const mfaFactorIdFromLocal = sessionStorage.getItem('admin_mfa_factor_id')

    if (mfaChallengeRequired && mfaFactorIdFromLocal) {
      setMFAFactorId(mfaFactorIdFromLocal)
      setShowMFAChallenge(true)
      sessionStorage.removeItem('admin_mfa_challenge_required')
      sessionStorage.removeItem('admin_mfa_factor_id')
      return
    }

    if (accessToken || type === 'magiclink') {
      handleMagicLinkCallback()
    } else {
      checkAuthStatus()
    }
  }, [location, handleMagicLinkCallback, navigate, processingCallback, showMFAChallenge, showMFAReset])

  const handleSendMagicLink = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      const result = await AdminService.sendMagicLink(email)

      if (result.success) {
        const from = getAdminReturnPath(location)
        localStorage.setItem('admin_login_next_path', from)
        console.log('📍 Saved next path for recovery:', from)

        setMagicLinkSent(true)
      } else {
        setError(result.error || 'Failed to send magic link')
      }
    } catch (error) {
      setError('An error occurred. Please try again.')
      console.error('Send magic link error:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleMFAVerify = async (e) => {
    e.preventDefault()

    if (mfaCode.length !== 6) {
      setError('Please enter a 6-digit code')
      return
    }

    try {
      setMfaVerifying(true)
      setError('')

      console.log('🔐 Verifying MFA code...')
      console.log('   Factor ID:', mfaFactorId)

      const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId: mfaFactorId
      })

      if (challengeError) {
        console.error('❌ Challenge error:', challengeError)
        setError(`Failed to create MFA challenge: ${challengeError.message}`)
        setMfaVerifying(false)
        return
      }

      console.log('✅ Challenge created:', challengeData.id)

      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId: mfaFactorId,
        challengeId: challengeData.id,
        code: mfaCode
      })

      if (verifyError) {
        console.error('❌ Verify error:', verifyError)
        setError('Invalid code. Please try again.')
        setMfaVerifying(false)
        setMFACode('')
        return
      }

      console.log('✅ MFA verified successfully!')

      localStorage.setItem('admin_mfa_challenge_completed', 'true')
      localStorage.setItem('admin_mfa_challenge_time', Date.now().toString())

      console.log('🔄 Refreshing session to update MFA status...')
      await supabase.auth.refreshSession()

      const { data: aalData } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel()
      console.log('✅ Checked MFA status level:', aalData?.currentLevel)

      if (aalData?.currentLevel !== 'aal2') {
        setError('Your verification has not completed yet. Please try again.')
        setMfaVerifying(false)
        return
      }

      const newSessionId = SessionManager.generateSessionId()
      SessionManager.setCurrentSession(newSessionId)

      sessionStorage.setItem('admin_mfa_verified_session_id', newSessionId)
      localStorage.setItem('admin_mfa_verified_session_id', newSessionId)
      console.log('🔗 MFA trust bonded to session:', newSessionId)

      sessionStorage.removeItem('admin_mfa_challenge_required')
      sessionStorage.removeItem('admin_mfa_factor_id')

      const currentUser = AdminService.getCurrentUser()

      if (currentUser) {
        console.log('✅ Using cached user data, proceeding...')
        onLoginSuccess(currentUser)

        const targetPath = getAdminReturnPath(location)
        localStorage.removeItem('admin_login_next_path')

        setTimeout(() => {
          navigate(targetPath, { replace: true })
        }, 800)
      } else {
        console.log('⚠️ No cached user, fetching profile...')
        const result = await AdminService.verifyAdminProfile()
        if (result.success) {
          const targetPath = getAdminReturnPath(location)
          localStorage.removeItem('admin_login_next_path')
          onLoginSuccess(result.user)
          setTimeout(() => {
            navigate(targetPath, { replace: true })
          }, 800)
        } else {
          setError(result.error)
          setMfaVerifying(false)
        }
      }

    } catch (error) {
      console.error('❌ MFA verification error:', error)
      setError(`Verification failed: ${error.message}`)
      setMfaVerifying(false)
    }
  }

  const handleResend = async () => {
    setMagicLinkSent(false)
    setError('')
  }

  // MFA RESET: Handle sending magic link with MFA reset flag
  const handleSendMFAResetLink = async (e) => {
    e.preventDefault()
    setMFAResetLoading(true)
    setError('')

    try {
      if (!mfaResetEmail || !mfaResetEmail.includes('@')) {
        setError('Please enter a valid email address')
        setMFAResetLoading(false)
        return
      }

      console.log('📧 Sending MFA reset link to:', mfaResetEmail)

      localStorage.setItem('admin_mfa_reset_requested', 'true')
      localStorage.setItem('admin_mfa_reset_email', mfaResetEmail)

      const { error } = await supabase.auth.signInWithOtp({
        email: mfaResetEmail,
        options: {
          emailRedirectTo: `${window.location.origin}/admin/login`,
          shouldCreateUser: false,
        },
      })

      if (error) {
        console.error('❌ MFA reset link error:', error)
        setError(error.message)
        setMFAResetLoading(false)
        return
      }

      console.log('✅ MFA reset link sent successfully')
      setMFAResetSent(true)

    } catch (error) {
      console.error('❌ Send MFA reset link error:', error)
      setError('Failed to send reset link. Please try again.')
    } finally {
      setMFAResetLoading(false)
    }
  }

  const handleBackToMFAChallenge = () => {
    setShowMFAReset(false)
    setMFAResetEmail('')
    setMFAResetSent(false)
    setError('')
  }

  // Processing callback state
  if (processingCallback) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4" dir="ltr">
        <div className="max-w-md w-full bg-white rounded-xl shadow-xl p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#A73A5D] mx-auto mb-4"></div>
          <p className="text-gray-600">Completing login...</p>
        </div>
      </div>
    )
  }

  // MFA Reset Request State
  if (showMFAReset) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4" dir="ltr">
        <div className="max-w-md w-full bg-white rounded-xl shadow-xl p-8">
          <div className="text-center mb-8">
            <div className="mx-auto w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-4">
              <Mail className="w-8 h-8 text-orange-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Reset Two-Factor Authentication</h1>
            <p className="text-gray-600 text-sm">
              Enter your email to receive a secure link that will reset your MFA setup.
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          {mfaResetSent ? (
            <div className="text-center space-y-6">
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <Mail className="w-8 h-8 text-green-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-2">Check Your Email</h2>
                <p className="text-gray-600 text-sm mb-4">
                  We&apos;ve sent a secure MFA reset link to <strong>{mfaResetEmail}</strong>
                </p>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-left">
                <p className="text-sm text-blue-800 mb-2">
                  <strong>What happens next:</strong>
                </p>
                <ol className="text-sm text-blue-700 space-y-1 pl-4 list-decimal">
                  <li>Click the link in your email</li>
                  <li>Your MFA will be automatically reset</li>
                  <li>You&apos;ll be redirected to set up a new authenticator</li>
                </ol>
              </div>
              <p className="text-xs text-gray-500">
                The link will expire in 1 hour for security reasons.
              </p>
              <button
                onClick={() => {
                  setMFAResetSent(false)
                  setMFAResetEmail('')
                }}
                className="text-[#A73A5D] hover:text-[#8F2F4D] font-medium text-sm"
              >
                Use a different email
              </button>
            </div>
          ) : (
            <form onSubmit={handleSendMFAResetLink} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="email"
                    value={mfaResetEmail}
                    onChange={(e) => setMFAResetEmail(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#A73A5D] focus:border-transparent"
                    placeholder="Enter your email"
                    required
                    disabled={mfaResetLoading}
                  />
                </div>
              </div>

              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <p className="text-sm text-orange-800">
                  <strong>⚠️ Important:</strong> Resetting your MFA will remove your current authenticator setup. You&apos;ll need to scan a new QR code and set up your authenticator app again.
                </p>
              </div>

              <button
                type="submit"
                disabled={mfaResetLoading}
                className="w-full bg-orange-600 hover:bg-orange-700 disabled:opacity-50 text-white font-semibold py-3 px-4 rounded-lg transition duration-200 flex items-center justify-center"
              >
                {mfaResetLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Sending Reset Link...
                  </>
                ) : (
                  <>
                    <Mail className="w-5 h-5 mr-2" />
                    Send Reset Link
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={handleBackToMFAChallenge}
                className="w-full text-gray-600 hover:text-gray-800 font-medium text-sm py-2"
              >
                ← Back to MFA verification
              </button>
            </form>
          )}
        </div>
      </div>
    )
  }

  // MFA Challenge state (for returning users)
  if (showMFAChallenge) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4" dir="ltr">
        <div className="max-w-md w-full bg-white rounded-xl shadow-xl p-8">
          <div className="text-center mb-8">
            <div className="mx-auto w-16 h-16 bg-[#A73A5D]/10 rounded-full flex items-center justify-center mb-4">
              <Lock className="w-8 h-8 text-[#A73A5D]" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">🔐 Verify Your Identity</h1>
            <p className="text-gray-600">Enter the 6-digit code from your authenticator app</p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={handleMFAVerify} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Authentication Code
              </label>
              <input
                type="text"
                value={mfaCode}
                onChange={(e) => setMFACode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#A73A5D] focus:border-transparent text-center text-2xl tracking-widest font-mono"
                placeholder="000000"
                maxLength={6}
                autoFocus
                disabled={mfaVerifying}
              />
              <p className="mt-2 text-xs text-gray-500 text-center">
                Open your authenticator app to get your code
              </p>
            </div>

            <button
              type="submit"
              disabled={mfaVerifying || mfaCode.length !== 6}
              className="w-full bg-[#A73A5D] hover:bg-[#8F2F4D] disabled:opacity-50 text-white font-semibold py-3 px-4 rounded-lg transition duration-200 flex items-center justify-center"
            >
              {mfaVerifying ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Verifying...
                </>
              ) : (
                <>
                  <Lock className="w-5 h-5 mr-2" />
                  Verify & Sign In
                </>
              )}
            </button>

            <div className="border-t border-gray-200 pt-6">
              <div className="text-center space-y-3">
                <p className="text-sm text-gray-600">
                  Lost access to your authenticator?
                </p>
                <button
                  type="button"
                  onClick={() => setShowMFAReset(true)}
                  className="text-[#A73A5D] hover:text-[#8F2F4D] font-medium text-sm underline underline-offset-2"
                >
                  Reset MFA setup
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    )
  }

  // Magic link sent state
  if (magicLinkSent) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4" dir="ltr">
        <div className="max-w-md w-full bg-white rounded-xl shadow-xl p-8">
          <div className="text-center">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <Mail className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Check Your Email</h1>
            <p className="text-gray-600 mb-6">
              We&apos;ve sent a secure login link to <strong>{email}</strong>
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-left">
              <p className="text-sm text-blue-800 mb-2">
                <strong>Next steps:</strong>
              </p>
              <ol className="text-sm text-blue-700 space-y-1 pl-4 list-decimal">
                <li>Open your email inbox</li>
                <li>Click the login link in the email</li>
                <li>You&apos;ll be redirected back here automatically</li>
              </ol>
            </div>
            <p className="text-xs text-gray-500 mb-4">
              The link will expire in 1 hour for security reasons.
            </p>
            <button
              onClick={handleResend}
              className="text-[#A73A5D] hover:text-[#8F2F4D] font-medium text-sm"
            >
              Didn&apos;t receive it? Send another link
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Initial login form
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4" dir="ltr">
      <div className="max-w-md w-full bg-white rounded-xl shadow-xl p-8">
        <div className="text-center mb-8">
          <div className="mx-auto w-16 h-16 bg-[#A73A5D]/10 rounded-full flex items-center justify-center mb-4">
            <Lock className="w-8 h-8 text-[#A73A5D]" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Admin Dashboard</h1>
          <p className="text-gray-600">Enter your email to receive a secure login link</p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}

        <form onSubmit={handleSendMagicLink} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Address
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#A73A5D] focus:border-transparent"
                placeholder="Enter your email"
                required
                disabled={loading}
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#A73A5D] hover:bg-[#8F2F4D] disabled:opacity-50 text-white font-semibold py-3 px-4 rounded-lg transition duration-200 flex items-center justify-center"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Sending Link...
              </>
            ) : (
              <>
                <Mail className="w-5 h-5 mr-2" />
                Send Magic Link
              </>
            )}
          </button>

          <div className="text-center">
            <p className="text-xs text-gray-500">
              🔒 Passwordless & secure authentication with two-factor protection
            </p>
          </div>
        </form>

        <div className="mt-8 text-center">
          <p className="text-xs text-gray-500">
            Wonder Beauties Admin Panel v2.0
          </p>
        </div>
      </div>
    </div>
  )
}

export default AdminLogin
